import React, { useState, useEffect } from "react";
import Sidebar, { FLAT_NAV } from "@/components/Sidebar";
import TopHeader from "@/components/TopHeader";
import UploadDialog from "@/components/UploadDialog";
import OverviewTab from "@/components/tabs/OverviewTab";
import CostProfitTab from "@/components/tabs/CostProfitTab";
import ShipmentsTab from "@/components/tabs/ShipmentsTab";
import WarehouseTab from "@/components/tabs/WarehouseTab";
import PurchaseOrdersTab from "@/components/tabs/PurchaseOrdersTab";
import TransactionsTab from "@/components/tabs/TransactionsTab";
import OrdersTab from "@/components/tabs/OrdersTab";
import OtherCostsTab from "@/components/tabs/OtherCostsTab";
import ProductHistoryTab from "@/components/tabs/ProductHistoryTab";
import AdminCmsTab from "@/components/tabs/AdminCmsTab";
import AdminPricingTab from "@/components/tabs/AdminPricingTab";
import UploadRegisterTab from "@/components/tabs/UploadRegisterTab";
import ReportsGuideTab from "@/components/tabs/ReportsGuideTab";
import BillingTab from "@/components/tabs/BillingTab";
import AdminPlansTab from "@/components/tabs/AdminPlansTab";
import PendingApproval from "@/pages/PendingApproval";
import { useAuth } from "@/context/AuthContext";

const FEATURE_OF = { overview: "overview", cost: "cost", shipments: "shipments", warehouse: "warehouse", po: "po", "tx-us": "tx", "tx-eu": "tx", orders: "orders", othercosts: "cost", history: "overview", register: "overview", guide: "guide" };

export default function Dashboard() {
  const { features, role, status } = useAuth();
  const expired = (status === "expired" || status === "trial-expired") && role !== "admin";
  const [active, setActive] = useState(() => (expired ? "billing" : "overview"));
  const [uploadOpen, setUploadOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const bump = () => setRefresh((n) => n + 1);

  const allowed = new Set(features || []);
  const feat = FEATURE_OF[active];
  const locked = ((feat && !allowed.has(feat)) || expired) && role !== "admin";

  useEffect(() => {
    if (expired || locked) setActive("billing");
  }, [expired, locked]);

  if (status === "pending" && role !== "admin") return <PendingApproval variant="pending" />;
  if (status === "suspended" && role !== "admin") return <PendingApproval variant="suspended" />;

  return (
    <div className="app-bg min-h-screen flex text-white">
      <Sidebar active={active} onChange={setActive} />
      <div className="flex-1 min-w-0 flex flex-col">
        <TopHeader active={active} onUpload={() => setUploadOpen(true)} />
        <main className="flex-1 p-4 md:p-8">
          {expired && (
            <div data-testid="trial-ended-banner" className="mb-4 rounded-xl border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-4 py-3 text-sm text-[#F59E0B]">
              Your trial has ended — choose a plan below to regain full access.
            </div>
          )}
          {/* Mobile nav */}
          <div className="md:hidden mb-4 flex gap-2 overflow-x-auto pb-1">
            {FLAT_NAV.map((n) => (
              <button
                key={n.id}
                data-testid={`mnav-${n.id}`}
                onClick={() => setActive(n.id)}
                className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-semibold border ${
                  active === n.id ? "bg-[#007AFF] border-[#007AFF] text-white" : "border-[#262626] text-[#A3A3A3]"
                }`}
              >
                {n.label}
              </button>
            ))}
          </div>

          <div key={`${active}-${refresh}`} className="fade-up">
            {active === "overview" && <OverviewTab onUpload={() => setUploadOpen(true)} />}
            {active === "cost" && <CostProfitTab />}
            {active === "shipments" && <ShipmentsTab onChanged={bump} />}
            {active === "warehouse" && <WarehouseTab key={refresh} />}
            {active === "po" && <PurchaseOrdersTab key={refresh} />}
            {active === "tx-us" && <TransactionsTab region="US" key={`us-${refresh}`} />}
            {active === "tx-eu" && <TransactionsTab region="EU" key={`eu-${refresh}`} />}
            {active === "orders" && <OrdersTab />}
            {active === "othercosts" && <OtherCostsTab />}
            {active === "history" && <ProductHistoryTab />}
            {active === "register" && <UploadRegisterTab onUpload={() => setUploadOpen(true)} />}
            {active === "guide" && <ReportsGuideTab />}
            {active === "billing" && <BillingTab />}
            {active === "admin" && role === "admin" && <AdminPlansTab />}
            {active === "cms" && role === "admin" && <AdminCmsTab />}
            {active === "pricing" && role === "admin" && <AdminPricingTab />}
          </div>
        </main>
      </div>
      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onDone={bump} />
    </div>
  );
}
